import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CalendarRange, ChevronDown, ChevronUp, Loader2, Plus, Save, Trash2, X } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import { PERIOD_KINDS, emptyYear, lastSessionDay, reportLine, shortDay } from "../../lib/groupCalendar";
import DatePickerField from "./DatePickerField";

const inp = "w-full rounded-lg border border-[#E5E7EB] bg-white px-2.5 py-1.5 text-sm text-[#373A45] focus:border-[#965EA7] focus:outline-none disabled:bg-[#F5F6F6]";
const quiet = "inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-semibold text-[#373A45] hover:bg-[#F5F6F6] disabled:opacity-40";

/**
 * Above the groups list: which live groups break a calendar rule of the
 * methodology, and the settings the rules read — the accreditation period
 * and the school years. The report only lists; fixing a group is opening it.
 */
export default function GroupCalendarPanel({ canEdit, refreshKey, onOpenGroup }) {
  const [report, setReport] = useState(null);
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        api.get("/admin/group-calendar/report"), api.get("/admin/group-calendar/settings"),
      ]);
      setReport(r.data);
      setSettings(s.data);
    } catch (e) {
      setReport(null);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!settings) return null;
  const count = report?.count || 0;
  const years = (settings.school_years || []).map((y) => y.label).join(", ");
  const acc = settings.accreditation || {};

  return (
    <div className={`mb-3 rounded-2xl border p-3 ${count ? "border-[#F5C76E]/60 bg-[#FFF7E6]" : "border-[#E5E7EB] bg-white"}`}
      data-testid="groups-calendar-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2 text-sm text-[#373A45]">
          {count
            ? <AlertTriangle size={15} className="mt-0.5 shrink-0 text-[#B7791F]" />
            : <CalendarRange size={15} className="mt-0.5 shrink-0 text-[#6B7280]" />}
          <span>
            {count ? <strong data-testid="groups-calendar-count">{reportLine(count)}</strong> : "Calendarul grupelor respectă regulile."}
            <span className="block text-xs text-[#6B7280]">
              Acreditare {acc.order} · până pe {shortDay(acc.end)} (ultima sesiune cel mai târziu pe{" "}
              {shortDay(lastSessionDay(acc.end, settings.evaluation_days))}) · an școlar introdus: {years || "niciunul"}
            </span>
          </span>
        </div>
        <div className="flex gap-2">
          {count > 0 && (
            <button type="button" className={quiet} onClick={() => setOpen((v) => !v)} data-testid="groups-calendar-toggle">
              {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {open ? "Ascunde" : "Vezi grupele"}
            </button>
          )}
          <button type="button" className={quiet} onClick={() => setEditing(true)} data-testid="groups-calendar-settings">
            <CalendarRange size={13} /> Calendarul anului școlar
          </button>
        </div>
      </div>

      {open && count > 0 && (
        <ul className="mt-3 space-y-2" data-testid="groups-calendar-report">
          {report.groups.map((g) => (
            <li key={g.id} className="rounded-xl border border-[#F5C76E]/40 bg-white p-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <strong className="text-sm text-[#373A45]">{g.name}</strong>
                <button type="button" className={quiet} onClick={() => onOpenGroup?.(g.id)}>Deschide grupa</button>
              </div>
              <ul className="mt-1 space-y-0.5 text-[11px] text-[#7A5A1B]">
                {g.problems.map((p, i) => (
                  <li key={`${p.code}-${i}`}>⚠ {p.message} <span className="opacity-75">({p.rule})</span></li>
                ))}
              </ul>
            </li>
          ))}
          <li className="text-[11px] text-[#6B7280]">
            Sesiunile acestea au fost salvate înainte de reguli. Grupa se poate edita în continuare;
            doar o sesiune nouă sau schimbată trebuie să le respecte.
          </li>
        </ul>
      )}

      {editing && (
        <CalendarSettings
          initial={settings} canEdit={canEdit}
          onClose={() => setEditing(false)}
          onSaved={(next) => { setSettings(next); setEditing(false); load(); }}
        />
      )}
    </div>
  );
}

function CalendarSettings({ initial, canEdit, onClose, onSaved }) {
  const [acc, setAcc] = useState(initial.accreditation);
  const [years, setYears] = useState(initial.school_years || []);
  const [saving, setSaving] = useState(false);

  const setYear = (i, patch) => setYears((ys) => ys.map((y, k) => (k === i ? { ...y, ...patch } : y)));
  const listOps = (i, field) => ({
    rows: years[i][field] || [],
    set: (rows) => setYear(i, { [field]: rows }),
  });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/group-calendar/settings", { accreditation: acc, school_years: years });
      toast.success("Am salvat calendarul.");
      onSaved(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Nu am putut salva calendarul."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4 backdrop-blur-sm" role="dialog"
      aria-modal="true" aria-label="Calendarul anului școlar" data-testid="groups-calendar-dialog">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-[#E5E7EB] p-4">
          <div>
            <h3 className="font-display text-base font-bold text-[#373A45]">Calendarul anului școlar și acreditarea</h3>
            <p className="mt-0.5 text-xs text-[#6B7280]">
              Regulile de calendar se verifică după datele de aici. Anul școlar se introduce o dată pe an, din ordinul de
              structură al ministerului; punțile se adaugă când le stabilește guvernul.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Închide" className="rounded-full p-1.5 hover:bg-[#F5F6F6]"><X size={16} /></button>
        </div>

        <div className="space-y-4 overflow-y-auto p-4">
          {!canEdit && (
            <p className="rounded-lg bg-[#F5F6F6] px-3 py-2 text-xs text-[#6B7280]">Doar proprietarul contului poate modifica aceste date.</p>
          )}
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Acreditarea</h4>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              <label className="text-xs text-[#6B7280] sm:col-span-1">
                Ordinul
                <input className={`${inp} mt-1`} value={acc.order || ""} disabled={!canEdit}
                  onChange={(e) => setAcc({ ...acc, order: e.target.value })} data-testid="calendar-acc-order" />
              </label>
              <label className="text-xs text-[#6B7280]">
                De la
                <div className="mt-1"><DatePickerField size="sm" value={acc.start} ariaLabel="Începutul acreditării"
                  onChange={(v) => canEdit && setAcc({ ...acc, start: v })} /></div>
              </label>
              <label className="text-xs text-[#6B7280]">
                Până la
                <div className="mt-1"><DatePickerField size="sm" value={acc.end} ariaLabel="Sfârșitul acreditării"
                  onChange={(v) => canEdit && setAcc({ ...acc, end: v })} /></div>
              </label>
            </div>
            <p className="mt-1 text-[11px] text-[#6B7280]">
              Evaluarea finală se face doar în perioada acreditării, iar ea vine la cel mult 30 de zile după ultima sesiune:
              ultima sesiune a unei grupe poate fi cel mai târziu pe <strong>{shortDay(lastSessionDay(acc.end))}</strong>.
            </p>
          </section>

          {years.map((y, i) => (
            <section key={i} className="rounded-xl border border-[#E5E7EB] p-3" data-testid={`calendar-year-${i}`}>
              <div className="grid gap-2 sm:grid-cols-4">
                <label className="text-xs text-[#6B7280]">
                  Anul școlar
                  <input className={`${inp} mt-1`} value={y.label} placeholder="2027–2028" disabled={!canEdit}
                    onChange={(e) => setYear(i, { label: e.target.value })} />
                </label>
                <label className="text-xs text-[#6B7280]">
                  Începe
                  <div className="mt-1"><DatePickerField size="sm" value={y.start} ariaLabel="Începutul anului școlar"
                    onChange={(v) => canEdit && setYear(i, { start: v })} /></div>
                </label>
                <label className="text-xs text-[#6B7280]">
                  Se încheie
                  <div className="mt-1"><DatePickerField size="sm" value={y.end} ariaLabel="Sfârșitul anului școlar"
                    onChange={(v) => canEdit && setYear(i, { end: v })} /></div>
                </label>
                <label className="text-xs text-[#6B7280]">
                  Sursa
                  <input className={`${inp} mt-1`} value={y.source || ""} placeholder="Ordinul MEC nr. …" disabled={!canEdit}
                    onChange={(e) => setYear(i, { source: e.target.value })} />
                </label>
              </div>

              <Periods {...listOps(i, "periods")} canEdit={canEdit} yearIndex={i} />
              <Days title="Zile fără cursuri" hint="până la 8 ore, ca sâmbăta (de exemplu, Ziua educației)"
                {...listOps(i, "days_off")} canEdit={canEdit} testid={`calendar-daysoff-${i}`} />
              <Days title="Punți" hint="fără activități de formare, ca în zilele libere legale"
                {...listOps(i, "bridges")} canEdit={canEdit} testid={`calendar-bridges-${i}`} />

              {canEdit && (
                <button type="button" className={`${quiet} mt-3 text-[#9b1f2c]`}
                  onClick={() => setYears((ys) => ys.filter((_, k) => k !== i))}>
                  <Trash2 size={13} /> Scoate anul {y.label}
                </button>
              )}
            </section>
          ))}
          {canEdit && (
            <button type="button" className={quiet} onClick={() => setYears((ys) => [...ys, emptyYear()])}
              data-testid="calendar-add-year">
              <Plus size={13} /> Adaugă un an școlar
            </button>
          )}
          <p className="text-[11px] text-[#6B7280]">
            Reguli fixe, care nu țin de setări: nimic în august, între 20 decembrie și 15 ianuarie, duminica și în zilele
            libere legale; nimic după 20:00; cel mult 4 ore pe zi în perioada de cursuri și 8 sâmbăta sau în vacanță;
            cel mult 25 de formabili pe grupă; același formator nu ține două grupe deodată.
          </p>
        </div>

        <div className="flex justify-end gap-2 border-t border-[#E5E7EB] p-3">
          <button type="button" className={quiet} onClick={onClose}>Închide</button>
          {canEdit && (
            <button type="button" onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#7A4986] disabled:opacity-40"
              data-testid="calendar-save">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Salvează
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Periods({ rows, set, canEdit, yearIndex }) {
  const update = (k, patch) => set(rows.map((r, j) => (j === k ? { ...r, ...patch } : r)));
  return (
    <div className="mt-3">
      <div className="text-xs font-semibold text-[#373A45]">Vacanțe și intervale</div>
      <div className="mt-1 space-y-1.5">
        {rows.map((p, k) => (
          <div key={k} className="grid items-center gap-2 sm:grid-cols-[8rem_8rem_1fr_1fr_auto]" data-testid={`calendar-period-${yearIndex}-${k}`}>
            <DatePickerField size="sm" value={p.from} ariaLabel="De la" onChange={(v) => canEdit && update(k, { from: v })} />
            <DatePickerField size="sm" value={p.to} ariaLabel="Până la" onChange={(v) => canEdit && update(k, { to: v })} />
            <select className={inp} value={p.kind} disabled={!canEdit} onChange={(e) => update(k, { kind: e.target.value })}
              aria-label="Felul intervalului">
              {PERIOD_KINDS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <input className={inp} value={p.name || ""} placeholder="numele (opțional)" disabled={!canEdit}
              onChange={(e) => update(k, { name: e.target.value })} aria-label="Numele intervalului" />
            {canEdit
              ? <button type="button" aria-label="Scoate intervalul" className="rounded-full p-1.5 text-[#9b1f2c] hover:bg-[#FDECEC]"
                  onClick={() => set(rows.filter((_, j) => j !== k))}><Trash2 size={13} /></button>
              : <span />}
          </div>
        ))}
      </div>
      <p className="mt-1 text-[11px] text-[#6B7280]">
        {PERIOD_KINDS.map((o) => `${o.label}: ${o.hint}`).join(" · ")}.
      </p>
      {canEdit && (
        <button type="button" className={`${quiet} mt-1.5`}
          onClick={() => set([...rows, { from: "", to: "", kind: "vacation", name: "" }])}>
          <Plus size={13} /> Adaugă un interval
        </button>
      )}
    </div>
  );
}

function Days({ title, hint, rows, set, canEdit, testid }) {
  const update = (k, patch) => set(rows.map((r, j) => (j === k ? { ...r, ...patch } : r)));
  return (
    <div className="mt-3" data-testid={testid}>
      <div className="text-xs font-semibold text-[#373A45]">{title} <span className="font-normal text-[#6B7280]">— {hint}</span></div>
      <div className="mt-1 space-y-1.5">
        {rows.length === 0 && <p className="text-[11px] text-[#6B7280]">Niciuna.</p>}
        {rows.map((d, k) => (
          <div key={k} className="grid items-center gap-2 sm:grid-cols-[8rem_1fr_auto]">
            <DatePickerField size="sm" value={d.date} ariaLabel={title} onChange={(v) => canEdit && update(k, { date: v })} />
            <input className={inp} value={d.name || ""} placeholder="numele (opțional)" disabled={!canEdit}
              onChange={(e) => update(k, { name: e.target.value })} aria-label={`Numele (${title})`} />
            {canEdit
              ? <button type="button" aria-label="Scoate ziua" className="rounded-full p-1.5 text-[#9b1f2c] hover:bg-[#FDECEC]"
                  onClick={() => set(rows.filter((_, j) => j !== k))}><Trash2 size={13} /></button>
              : <span />}
          </div>
        ))}
      </div>
      {canEdit && (
        <button type="button" className={`${quiet} mt-1.5`} onClick={() => set([...rows, { date: "", name: "" }])}>
          <Plus size={13} /> Adaugă o zi
        </button>
      )}
    </div>
  );
}
